/**
 * Writing an email (logic/mail): a new message, a reply (threaded: In-Reply-To/References on the server) or a
 * forward, from one of my mailboxes. To/Cc/Bcc are typed as text and parsed (`parseAddressList`); attachments are
 * files I uploaded (FileShare uploads, purpose "mail"). Sending needs the network; a refused send keeps the draft.
 * Like Gmail, what I write is saved as a draft a moment after I stop typing (Drafts folder), sending can wait for a
 * time (scheduled send) or for the undo window, and Discard deletes the saved draft.
 */
import type { Locale, MailAddress, MailAssistAction, MailDraft, MailTemplate, SendMailResponse } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { MAIL_RECIPIENTS_MAX, MAIL_TEXT_MAX, composerTail, draftReplyKind, formatAddress, invalidAddressParts, joinComposer, joinReplyText, parseAddressList, splitComposer, splitReplyText, switchRecipients, withSignature, type ReplyKind, type ReplyTargets } from "../logic/mail.ts";
import { ApiFailure, errorKind } from "../ports.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ComposerStart = {
  mailboxId: string;
  mode: "new" | "reply" | "forward";
  /** The From to start with (a reply: logic/mailIdentity replyFrom); absent = the mailbox's default. */
  from?: string;
  /** The message replied to / forwarded. */
  sourceMessageId?: string;
  to: MailAddress[];
  cc: MailAddress[];
  subject: string;
  text: string;
  /** A forward's files from the original (their attachment ids go in `fileIds`; the server copies them). */
  attachments?: ComposerAttachment[];
  /** The original's files: switching to Forward brings them, leaving it takes them back. */
  forwardFiles?: ComposerAttachment[];
  /** Reopening a saved draft (Drafts folder, or an undone send): its id and fields win. */
  draft?: MailDraft;
  /** Started as this kind of reply: with `targets`, the box switches kind in place (`switchReply`). */
  kind?: ReplyKind;
  targets?: ReplyTargets;
  /** A saved Reply draft opened with Reply all: the box switches to it, adding the people only Reply all brings. */
  switchTo?: ReplyKind;
};

/** How long after the last keystroke the draft is saved. */
export const DRAFT_SAVE_MS = 1500;
/** Gmail's default undo-send window. */
export const UNDO_SEND_MS = 10_000;

export type ComposerAttachment = { fileId: string; name: string; size: number };

export type MailComposerState = {
  mailboxId: string;
  mode: ComposerStart["mode"];
  /** A reply box's kind (`switchReply`); absent where it can't switch. */
  replyKind?: ReplyKind;
  sourceMessageId?: string;
  /** The From chosen (one of the mailbox's sendAs); absent = its default. */
  from?: string;
  to: string;
  cc: string;
  bcc: string;
  /** Cc/Bcc fields shown. */
  showCc: boolean;
  subject: string;
  text: string;
  attachments: ComposerAttachment[];
  sending: boolean;
  /** Recipients that couldn't be read (shown under the field). */
  invalid: string[];
  error?: MessageDescriptor;
  /** The sent message (the screen closes the composer). */
  sent?: SendMailResponse;
  /** The draft this is saved as (Drafts folder). */
  draftId: string;
  /** When the draft was last saved ("Draft saved"). */
  savedAt?: number;
  /** The last autosave failed: what's on screen isn't in Drafts yet (the next edit tries again). */
  saveFailed?: boolean;
  /** "Help me write" is writing the body. */
  assisting: boolean;
  /** "Help me write" failed. */
  assistError?: MessageDescriptor;
  /** "Remind me if no reply" at this time (sent with the message). */
  remindAt?: number;
  /** My templates, once loaded (the composer's Templates menu). */
  templates?: MailTemplate[];
  /**
   * "Sent with Bundle" under my signature (logic/mailFooter): "on" shows the line and the send asks for it; "removed"
   * is my ✕ on this message (the signature's menu offers it back); absent when my setting or the workspace has it off.
   */
  sentWith?: "on" | "removed";
};

const list = (as: MailAddress[]) => as.map(formatAddress).join(", ");

/** Why a send failed, in words: what's wrong is often something I can fix (or an admin can). */
function sendFailure(e: unknown): MessageDescriptor {
  const code = e instanceof ApiFailure ? e.code : "";
  if (code === "plan_limit_storage") return msg("mail.compose.failedStorage");
  if (code === "billing_required") return msg("mail.compose.failedBilling");
  if (code === "conflict") return msg("mail.compose.failedDomain");
  switch (errorKind(e)) {
    case "network": return msg("mail.compose.failedNetwork");
    case "rateLimited": return msg("mail.compose.failedLimit");
    case "notFound": return msg("mail.compose.failedGone");
    case "forbidden": return msg("mail.compose.failedForbidden");
    case "invalid": return msg("mail.compose.failedRefused");
    default: return msg("mail.compose.failed");
  }
}

export class MailComposerViewModel extends ViewModel<MailComposerState> {
  private cancelSave: (() => void) | undefined;
  /** Typed into since it was opened (an untouched composer leaves no draft behind). */
  private dirty = false;
  private saved = false;
  private discarded = false;
  private readonly targets: ReplyTargets | undefined;
  private readonly forwardFiles: ComposerAttachment[];
  /** The fields I left for Forward or left Forward from, so going back restores them. */
  private parked: { to: string; cc: string; kind: ReplyKind } | undefined;
  private forwardTo: { to: string; cc: string } = { to: "", cc: "" };

  /**
   * `signature`: the one this message starts with (logic/mail `signatureFor`: my mailbox's, if it's on for this mode).
   * `sentWith`: whether "Sent with Bundle" goes on this mailbox's messages (logic/mailFooter `wantsSentWith`, before ✕).
   */
  constructor(private readonly ws: WorkspaceViewModel, start: ComposerStart, signature?: string, sentWith = false) {
    const d = start.draft;
    const text = d !== undefined ? d.text : withSignature(start.text, signature);
    const s: MailComposerState = {
      mailboxId: d?.mailboxId ?? start.mailboxId, mode: d?.mode ?? start.mode, to: d?.to ?? list(start.to), cc: d?.cc ?? list(start.cc), bcc: d?.bcc ?? "",
      showCc: (d?.cc ?? "") !== "" || (d?.bcc ?? "") !== "" || start.cc.length > 0, subject: d?.subject ?? start.subject,
      text, attachments: (d?.attachments ?? start.attachments ?? []).map((a) => ({ fileId: a.fileId, name: a.name, size: a.size })), sending: false, assisting: false, invalid: [], draftId: d?.id ?? ws.deps.ids.next(),
    };
    const source = d?.sourceMessageId ?? start.sourceMessageId;
    if (source !== undefined) s.sourceMessageId = source;
    const from = d?.from ?? start.from;
    if (from !== undefined) s.from = from;
    if (sentWith) s.sentWith = d?.sentWith === false ? "removed" : "on";
    const kind = d !== undefined && start.targets !== undefined ? draftReplyKind(d.mode, parseAddressList(d.to), parseAddressList(d.cc), start.targets) : start.kind;
    if (kind !== undefined) s.replyKind = kind;
    super(s);
    this.targets = start.targets;
    this.forwardFiles = start.forwardFiles ?? [];
    this.saved = d !== undefined;
    this.onDispose(() => this.cancelSave?.());
    if (start.switchTo !== undefined) this.switchReply(start.switchTo);
  }

  // Fields echo unchanged values back (focus, re-renders): those aren't edits and save no draft.
  setTo(to: string): void {
    if (to !== this.state.to) this.edit({ to, invalid: [] });
  }
  setCc(cc: string): void {
    if (cc !== this.state.cc) this.edit({ cc, invalid: [] });
  }
  setBcc(bcc: string): void {
    if (bcc !== this.state.bcc) this.edit({ bcc, invalid: [] });
  }
  showCcBcc(): void {
    this.update({ showCc: true });
  }
  /** The From address (one of the mailbox's sendAs). */
  setFrom(from: string): void {
    if (from !== this.state.from) this.edit({ from });
  }
  setSubject(subject: string): void {
    if (subject !== this.state.subject) this.edit({ subject });
  }
  setText(text: string): void {
    if (text !== this.state.text) this.edit({ text });
  }

  /**
   * Gmail's "Help me write": the body is written from `prompt` (replying, from the original too) and replaces what I
   * wrote above my signature and the quoted original, which stay.
   */
  async assist(prompt: string, action: MailAssistAction = "draft", language?: string): Promise<void> {
    if (this.state.assisting) return;
    if ((action === "draft" || action === "ask") && prompt.trim() === "") return;
    const tail = composerTail(this.state.text);
    const current = this.state.text.slice(0, this.state.text.length - tail.length).trim();
    if (action !== "draft" && current === "") return;
    const { assistError: _cleared, ...rest } = this.state;
    void _cleared;
    this.setState({ ...rest, assisting: true });
    try {
      const text = await this.ws.deps.api.mailAssist(this.ws.wsId, this.state.mailboxId, {
        prompt: prompt.trim(),
        ...(action === "draft" ? {} : { action }),
        ...(language === undefined ? {} : { language }),
        ...(current === "" ? {} : { current }),
        ...(this.state.mode === "reply" && this.state.sourceMessageId !== undefined ? { replyToMessageId: this.state.sourceMessageId } : {}),
      });
      if (this.isDisposed) return;
      this.update({ assisting: false });
      this.edit({ text: `${text}${tail}` });
    } catch {
      if (!this.isDisposed) this.update({ assisting: false, assistError: msg("mail.compose.assistFailed") });
    }
  }

  /** What I write, keeping the signature and the quoted original below it (Spark's separate signature block). */
  setBody(body: string): void {
    this.setText(joinComposer({ ...splitComposer(this.state.text), body }));
  }

  /** What I write in a reply box, with the quote kept beside it. */
  setMain(main: string): void {
    this.setText(joinReplyText({ main, quote: splitReplyText(this.state.text).quote }));
  }

  /** The quoted original, edited in its own field. */
  setQuote(quote: string): void {
    this.setText(joinReplyText({ main: splitReplyText(this.state.text).main, quote }));
  }

  /** This reply goes without the quoted original. */
  removeQuote(): void {
    this.setText(splitReplyText(this.state.text).main);
  }

  /**
   * Reply, Reply all or Forward, in the same box: what I wrote, its attachments and the draft stay, and only who it
   * goes to changes (`switchRecipients`) and the subject, unless I edited it. Forward starts with no one; going back
   * restores who it went to.
   */
  switchReply(kind: ReplyKind): void {
    const t = this.targets;
    const cur = this.state.replyKind;
    if (t === undefined || cur === undefined || kind === cur) return;
    const subject = (k: ReplyKind) => (k === "forward" ? t.forwardSubject : t.subject);
    const patch: Partial<MailComposerState> = { replyKind: kind, mode: kind === "forward" ? "forward" : "reply", invalid: [] };
    if (this.state.subject === subject(cur)) patch.subject = subject(kind);
    const ids = new Set(this.forwardFiles.map((a) => a.fileId));
    const files = this.state.attachments.filter((a) => !ids.has(a.fileId));
    patch.attachments = kind === "forward" ? [...this.forwardFiles, ...files].slice(0, 20) : files;
    if (kind === "forward") {
      this.parked = { to: this.state.to, cc: this.state.cc, kind: cur };
      patch.to = this.forwardTo.to;
      patch.cc = this.forwardTo.cc;
    } else {
      if (cur === "forward") this.forwardTo = { to: this.state.to, cc: this.state.cc };
      const base = cur === "forward" ? (this.parked ?? { to: "", cc: "", kind }) : { to: this.state.to, cc: this.state.cc, kind: cur };
      const next = switchRecipients({ to: parseAddressList(base.to), cc: parseAddressList(base.cc) }, t[base.kind === "forward" ? kind : base.kind], t[kind]);
      // An entry that isn't an address yet stays, for me to fix.
      patch.to = [...next.to.map(formatAddress), ...invalidAddressParts(base.to)].join(", ");
      patch.cc = [...next.cc.map(formatAddress), ...invalidAddressParts(base.cc)].join(", ");
      if (patch.cc !== "") patch.showCc = true;
    }
    this.edit(patch);
  }

  /** "Add from contacts": into To, unless it's already a recipient. */
  addRecipient(a: MailAddress): void {
    const have = [this.state.to, this.state.cc, this.state.bcc].flatMap(parseAddressList).map((x) => x.address.toLowerCase());
    if (have.includes(a.address.toLowerCase())) return;
    const to = this.state.to.trim().replace(/[,;]$/, "").trim();
    this.setTo(to === "" ? formatAddress(a) : `${to}, ${formatAddress(a)}`);
  }

  /** The signature block, edited in place (this message only); emptied, the message goes without one. */
  setSignature(signature: string): void {
    this.setText(joinComposer({ ...splitComposer(this.state.text), signature: signature.trim() === "" ? undefined : signature }));
  }

  /** The signature's ✕: this message goes without it. */
  removeSignature(): void {
    this.edit({ text: joinComposer({ ...splitComposer(this.state.text), signature: undefined }) });
  }

  /** The ✕ on "Sent with Bundle": this message goes without it. */
  removeSentWith(): void {
    if (this.state.sentWith === "on") this.edit({ sentWith: "removed" });
  }

  /** "Add 'Sent with Bundle'" (the signature's menu): back on this message. */
  addSentWith(): void {
    if (this.state.sentWith === "removed") this.edit({ sentWith: "on" });
  }

  /** "Remind me if no reply" (undefined: no reminder). */
  setRemind(at: number | undefined): void {
    const { remindAt: _old, ...rest } = this.state;
    void _old;
    this.setState(at === undefined ? rest : { ...rest, remindAt: at });
  }

  async loadTemplates(): Promise<void> {
    try {
      const templates = await this.ws.deps.api.listMailTemplates(this.ws.wsId);
      if (!this.isDisposed) this.update({ templates });
    } catch {
      if (!this.isDisposed) this.update({ templates: [] });
    }
  }

  /** Starts from a template: its text replaces what I wrote; its subject fills an empty subject. */
  applyTemplate(t: MailTemplate): void {
    this.edit({ text: joinComposer({ ...splitComposer(this.state.text), body: t.text }), ...(this.state.subject.trim() === "" && t.subject !== "" ? { subject: t.subject } : {}) });
  }

  /** Saves what I wrote (and the subject) as a template. */
  async saveAsTemplate(name: string): Promise<MailTemplate | undefined> {
    const body = splitComposer(this.state.text).body;
    if (name.trim() === "" || body.trim() === "") return undefined;
    try {
      const t = await this.ws.deps.api.saveMailTemplate(this.ws.wsId, undefined, { name: name.trim(), subject: this.state.subject.trim(), text: body });
      if (!this.isDisposed) this.update({ templates: [...(this.state.templates ?? []), t].sort((a, b) => a.name.localeCompare(b.name)) });
      return t;
    } catch {
      return undefined;
    }
  }

  /** Saves the draft now (also on close when there are unsaved changes). */
  async saveDraft(): Promise<void> {
    this.cancelSave?.();
    this.cancelSave = undefined;
    if (!this.dirty || this.discarded || this.state.sent !== undefined || !this.ws.state.network) return;
    const s = this.state;
    try {
      await this.ws.deps.api.saveMailDraft(this.ws.wsId, s.mailboxId, s.draftId, {
        mode: s.mode, to: s.to, cc: s.cc, bcc: s.bcc, subject: s.subject, text: s.text,
        ...(s.sourceMessageId === undefined ? {} : { sourceMessageId: s.sourceMessageId }),
        ...(s.from === undefined ? {} : { from: s.from }),
        ...(s.attachments.length === 0 ? {} : { attachments: s.attachments.map((a) => ({ fileId: a.fileId, name: a.name, size: a.size })) }),
        ...(s.sentWith === "removed" ? { sentWith: false } : {}),
      });
      this.saved = true;
      this.dirty = false;
      if (!this.isDisposed) this.update({ savedAt: this.ws.deps.clock.now(), saveFailed: false });
    } catch {
      // Kept in the composer; the next keystroke tries again.
      if (!this.isDisposed) this.update({ saveFailed: true });
    }
  }

  /** Tries to save what's unsaved; false if text on screen still isn't in Drafts (offline, or the save failed). */
  async flushDraft(): Promise<boolean> {
    await this.saveDraft();
    return !this.dirty || this.discarded || this.state.sent !== undefined;
  }

  /** What's on screen as a draft. */
  currentDraft(): MailDraft {
    const s = this.state;
    return {
      id: s.draftId, mailboxId: s.mailboxId, mode: s.mode, to: s.to, cc: s.cc, bcc: s.bcc, subject: s.subject, text: s.text, updatedAt: this.ws.deps.clock.now(),
      ...(s.from === undefined ? {} : { from: s.from }),
      ...(s.sourceMessageId === undefined ? {} : { sourceMessageId: s.sourceMessageId }),
      ...(s.attachments.length === 0 ? {} : { attachments: s.attachments.map((a) => ({ fileId: a.fileId, name: a.name, size: a.size })) }),
      ...(s.sentWith === "removed" ? { sentWith: false } : {}),
    };
  }

  /** The draft a closing reply box leaves behind: none once it's sent, discarded or never written in. */
  leftDraft(): MailDraft | undefined {
    return this.discarded || this.state.sent !== undefined || !(this.saved || this.dirty) ? undefined : this.currentDraft();
  }

  /** Discard: the composer closes and its saved draft goes. */
  async discard(): Promise<void> {
    this.cancelSave?.();
    this.cancelSave = undefined;
    this.dirty = false;
    this.discarded = true;
    if (this.saved) await this.ws.deps.api.deleteMailDraft(this.ws.wsId, this.state.draftId).catch(() => undefined);
  }

  private edit(patch: Partial<MailComposerState>): void {
    this.update(patch);
    this.dirty = true;
    this.cancelSave?.();
    this.cancelSave = this.ws.deps.clock.setTimer(DRAFT_SAVE_MS, () => {
      this.cancelSave = undefined;
      void this.saveDraft();
    });
  }
  /** `sentWith`: the new mailbox's (as in the constructor); absent, the line stays as it is. A ✕ stays. */
  setMailbox(mailboxId: string, sentWith?: boolean): void {
    // Another mailbox has other addresses: its default From.
    const { from: _from, sentWith: line, ...rest } = this.state;
    void _from;
    if (mailboxId === this.state.mailboxId) return;
    const next = sentWith === undefined ? line : sentWith ? (line ?? "on") : undefined;
    this.setState({ ...rest, mailboxId, ...(next === undefined ? {} : { sentWith: next }) });
  }
  addAttachment(a: ComposerAttachment): void {
    if (this.state.attachments.length >= 20 || this.state.attachments.some((x) => x.fileId === a.fileId)) return;
    this.edit({ attachments: [...this.state.attachments, a] });
  }
  removeAttachment(fileId: string): void {
    this.edit({ attachments: this.state.attachments.filter((a) => a.fileId !== fileId) });
  }

  /**
   * Sends: recipients must parse and there must be at least one. `sendAt`: scheduled send, or now + UNDO_SEND_MS for
   * the undo window. `locale`: the app language the composer shows "Sent with Bundle" in, so the message has the same
   * line (absent: the server uses my Member.locale). → the response, or undefined when refused.
   */
  async send(sendAt?: number, locale?: Locale): Promise<SendMailResponse | undefined> {
    if (this.state.sending) return undefined;
    const invalid = [...invalidAddressParts(this.state.to), ...invalidAddressParts(this.state.cc), ...invalidAddressParts(this.state.bcc)];
    const to = parseAddressList(this.state.to);
    const cc = parseAddressList(this.state.cc);
    const bcc = parseAddressList(this.state.bcc);
    if (invalid.length > 0) {
      this.update({ invalid, error: msg("mail.compose.invalidRecipients", { count: invalid.length }) });
      return undefined;
    }
    if (to.length + cc.length + bcc.length === 0) {
      this.update({ error: msg("mail.compose.noRecipients") });
      return undefined;
    }
    if (to.length + cc.length + bcc.length > MAIL_RECIPIENTS_MAX) {
      this.update({ error: msg("mail.compose.tooManyRecipients", { max: MAIL_RECIPIENTS_MAX }) });
      return undefined;
    }
    if (this.state.text.length > MAIL_TEXT_MAX) {
      this.update({ error: msg("mail.compose.tooLong") });
      return undefined;
    }
    if (!this.ws.state.network) {
      this.update({ error: msg("mail.compose.offline") });
      return undefined;
    }
    const { error: _cleared, ...rest } = this.state;
    void _cleared;
    this.setState({ ...rest, sending: true, invalid: [] });
    this.cancelSave?.();
    this.cancelSave = undefined;
    try {
      const req = {
        draftId: this.state.draftId,
        // The body is the composer's markdown (formatting bar): the server sends it as HTML plus plain text.
        format: "markdown" as const,
        ...(sendAt === undefined ? {} : { sendAt }),
        ...(this.state.from === undefined ? {} : { from: this.state.from }),
        ...(this.state.remindAt === undefined ? {} : { remindAt: this.state.remindAt }),
        // The line the composer showed under my signature: the server adds it (apps/api mail/compose).
        ...(this.state.sentWith === "on" ? { sentWith: true, ...(locale === undefined ? {} : { sentWithLocale: locale }) } : {}),
        to, cc, bcc, subject: this.state.subject.trim(), text: this.state.text, fileIds: this.state.attachments.map((a) => a.fileId),
        ...(this.state.mode === "reply" && this.state.sourceMessageId !== undefined ? { replyToMessageId: this.state.sourceMessageId } : {}),
        ...(this.state.mode === "forward" && this.state.sourceMessageId !== undefined ? { forwardOfMessageId: this.state.sourceMessageId } : {}),
      };
      const sent = await this.ws.deps.api.sendMail(this.ws.wsId, this.state.mailboxId, req);
      this.dirty = false;
      if (!this.isDisposed) this.update({ sending: false, sent });
      return sent;
    } catch (e) {
      if (!this.isDisposed) this.update({ sending: false, error: sendFailure(e) });
      return undefined;
    }
  }
}

